/**
 * Player ratings, drawn inside the career screen.
 *
 * public/js/career.js calls renderRatings(rootEl, data, ctx). Pass data as
 * null and this module fetches /api/ratings itself, so the career screen only
 * has to hand over an element.
 *
 * There is no overall number on this screen and there must never be one. Nine
 * traits, each 1 to 5, and the line each one has moved along over the months.
 * A single score on a kid reads as a scouting grade, which is exactly what the
 * Worker refuses to produce, and drawing one here out of the nine traits would
 * put it back.
 *
 * Every visibility rule is the Worker's, not this screen's. A scope the viewer
 * may not see arrives with visible:false and a sentence saying why, and this
 * file prints that sentence. It never decides anything itself, and it never
 * has the hidden numbers to leak in the first place.
 */

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/* Read as a fallback only. The Worker sends the real list on every response. */
const FALLBACK_TRAITS = [
  { key: 'shooting', label: 'Shooting' }, { key: 'finishing', label: 'Finishing' },
  { key: 'passing', label: 'Passing' }, { key: 'handles', label: 'Handles' },
  { key: 'defense', label: 'Defense' }, { key: 'rebounding', label: 'Rebounding' },
  { key: 'motor', label: 'Motor' }, { key: 'iq', label: 'Basketball IQ' },
  { key: 'coachability', label: 'Coachability' },
];

/** What the person doing the rating is told, before they rate. */
const SCOPE_HELP = {
  self: 'Only you see this. Rate it honestly, the point is watching it move.',
  coach: 'This team\'s coaches and the player\'s own family group see this. The player sees it only if you share it with them.',
  teammate: 'Pooled with at least two other teammates before anybody sees it, and never with your name on it.',
  public: 'Anyone can see this one, and it is for players 18 and up on both sides.',
};
const SCOPE_ORDER = ['self', 'coach', 'teammate', 'public'];

let C = null;        // the app context: api, toast, ui
let ROOT = null;
const S = {
  data: null, err: null, loading: false,
  people: null, peopleErr: null, peopleReason: '',
  form: null,        // { who, label, scope, scopes, traits, note, shared, busy }
};

const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : null; };
const one = (n) => (Math.abs(n - Math.round(n)) < 0.05 ? String(Math.round(n)) : String(Math.round(n * 10) / 10));
const traitList = () => (Array.isArray(S.data?.traits) && S.data.traits.length ? S.data.traits : FALLBACK_TRAITS);

/** ctx.api when the app gave us one, a plain fetch when it did not. */
async function api(url, opts) {
  if (C?.api) return C.api(url, opts);
  const r = await fetch(url, { headers: { 'content-type': 'application/json' }, ...opts });
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `Request failed (${r.status})`);
  return body;
}
const toast = (msg, isError) => { if (C?.toast) C.toast(msg, isError); };

/* ------------------------------------------------------------------ entry */

/**
 * Safe to call repeatedly, safe with no data at all, and it never throws: an
 * API that is down shows its message in the panel with a way to try again.
 */
export async function renderRatings(rootEl, data, ctx) {
  if (!rootEl) return;
  if (ctx) C = ctx;
  if (ROOT !== rootEl) rebind(rootEl);
  if (data && typeof data === 'object') { S.data = data; S.err = null; paint(); return; }
  if (!S.data) paint();                    // never leave the panel blank while the first call is out
  await refresh();
}

async function refresh() {
  if (S.loading) return;
  S.loading = true;
  try { S.data = await api('/api/ratings'); S.err = null; }
  catch (e) { S.err = e.message; }
  S.loading = false;
  paint();
}

/* ------------------------------------------------------------------ paint */

function paint() {
  if (!ROOT) return;
  if (S.err) {
    ROOT.innerHTML = `<div class="card"><h3>Ratings</h3><p class="sub">${esc(S.err)}</p>
      <button class="btn" data-act="retry" style="margin-top:12px">Try again</button></div>`;
    return;
  }
  if (!S.data) { ROOT.innerHTML = '<div class="card"><h3>Ratings</h3><p class="sub">Loading your ratings.</p></div>'; return; }
  ROOT.innerHTML = `${readCard()}${S.form ? formCard() : ''}`;
}

function readCard() {
  const scopes = S.data.scopes || {};
  const blocks = SCOPE_ORDER.map((key) => scopeBlock(scopes[key], key)).filter(Boolean).join('');
  // A player with nothing logged gets an invitation, not four closed doors.
  const bare = SCOPE_ORDER.every((key) => !(num(scopes[key]?.count) > 0));
  return `
    <div class="card rcard">
      <h3>How you are rated</h3>
      <p class="rlede">${esc(S.data.noOverall || '')}</p>
      ${bare ? `<p class="rinvite">Nothing here yet. Start with your own read: nine things, 1 to 5, and it takes about a minute.
        The part worth having is the line each one draws once you rate yourself again next month.</p>` : ''}
      <div class="rscopes">${blocks || '<p class="sub">Nothing to show yet.</p>'}</div>
      ${scaleNote(scopes)}
      <div class="ractions">
        <button class="btn primary" data-act="self">Rate yourself</button>
        <button class="btn" data-act="other">Rate someone on your team</button>
      </div>
      ${S.data.publicNote ? `<p class="rplain">${esc(S.data.publicNote)}</p>` : ''}
    </div>`;
}

function scopeBlock(scope, key) {
  const label = esc(scope?.label || key);
  if (!scope || scope.visible === false) {
    return `<section class="rscope off">
      <div class="rscope-h"><b>${label}</b><span class="pill">Not shown</span></div>
      <p class="sub">${esc(scope?.reason || 'Not shown here.')}</p>
    </section>`;
  }
  const count = num(scope.count) || 0;
  if (!count) {
    return `<section class="rscope off">
      <div class="rscope-h"><b>${label}</b><span class="pill">None yet</span></div>
      <p class="sub">${esc(scope.reason || emptyLine(key))}</p>
    </section>`;
  }
  const pooled = scope.attributed === false;
  return `<section class="rscope">
    <div class="rscope-h">
      <b>${label}</b>
      <span class="pill ${pooled ? 'cool' : 'hot'}">${count} rating${count === 1 ? '' : 's'}</span>
      ${pooled ? '<span class="rtag">pooled, nobody named</span>' : ''}
    </div>
    ${scope.reason ? `<p class="sub">${esc(scope.reason)}</p>` : ''}
    <div class="rtraits">${traitList().map((t) => traitRow(t, scope)).join('')}</div>
    ${entriesList(scope)}
  </section>`;
}

/**
 * What the two numbers on a trait line actually cover, said once for the card.
 * An average of every rating in a group sitting next to a change measured from
 * the first month rated is two different windows on one row, and a number
 * nobody can place is halfway to a made up one.
 */
function scaleNote(scopes) {
  const shown = SCOPE_ORDER.map((key) => scopes[key]).filter((s) => s && s.visible !== false && num(s.count) > 0);
  if (!shown.length) return '';
  const moved = shown.some((s) => Array.isArray(s.trend) && s.trend.length > 1);
  return `<p class="rscale">Each number is the average of every rating in that group, 1 to 5.${
    moved ? ' A change is measured from the first month rated to the most recent.' : ''}</p>`;
}

const emptyLine = (key) => ({
  self: 'You have not rated yourself yet. It takes a minute, and the point is watching it move.',
  coach: 'No coach has shared a rating with you yet.',
  teammate: 'No teammate ratings yet.',
  public: 'No public ratings yet.',
}[key] || 'Nothing here yet.');

function traitRow(t, scope) {
  const cell = scope.traits?.[t.key];
  const avg = num(cell?.average);
  if (avg === null) {
    return `<div class="rtrait none"><span class="rt-name">${esc(t.label)}</span>
      <span class="rbar"><i style="width:0"></i></span><span class="rt-val">not rated</span></div>`;
  }
  const pct = Math.max(0, Math.min(100, ((avg - 1) / 4) * 100));
  const series = seriesFor(scope.trend, t.key);
  const move = series.length > 1 ? Math.round((series.at(-1).v - series[0].v) * 10) / 10 : null;
  // The window the change covers, spelled out, because "down 3" on its own
  // says nothing about whether that happened over a month or over four years.
  const span = move === null ? ''
    : `${one(series[0].v)} in ${monthName(series[0].month)}, ${one(series.at(-1).v)} in ${monthName(series.at(-1).month)}`;
  return `<div class="rtrait">
    <span class="rt-name">${esc(t.label)}</span>
    <span class="rbar" role="img" aria-label="${esc(t.label)} ${one(avg)} out of 5"><i style="width:${pct.toFixed(1)}%"></i></span>
    <span class="rt-val"><b>${one(avg)}</b><small>of 5</small></span>
    ${spark(series, t.label, span)}
    <span class="rt-move${move > 0 ? ' up' : move < 0 ? ' down' : ''}"${span ? ` title="${esc(span)}"` : ''}>${
      move === null ? '' : move === 0 ? 'level' : `${move > 0 ? 'up' : 'down'} ${one(Math.abs(move))}`}</span>
  </div>`;
}

/** '2026-09' as 'Sep 2026'. Anything else comes back as it arrived. */
function monthName(key) {
  const [y, mo] = String(key || '').split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return String(key || '');
  return new Date(Date.UTC(y, mo - 1, 1)).toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** One trait's monthly points, in real month order with the real gaps kept. */
function seriesFor(trend, key) {
  const out = [];
  for (const point of Array.isArray(trend) ? trend : []) {
    const v = num(point?.traits?.[key]?.average);
    const [y, mo] = String(point?.month || '').split('-').map(Number);
    if (v === null || !Number.isFinite(y) || !Number.isFinite(mo)) continue;
    out.push({ ord: y * 12 + mo, v, month: point.month });
  }
  return out.sort((a, b) => a.ord - b.ord);
}

/**
 * A sparkline for one trait. Months with nothing rated are a real gap in time,
 * so the x axis is the month itself and not the position in the list: two
 * ratings a year apart must not draw like two in a row.
 */
function spark(series, label = '', range = '') {
  if (series.length < 2) return '<span class="rspark-none"></span>';
  const w = 62, h = 20, pad = 2.5;
  const first = series[0].ord, last = series.at(-1).ord;
  const span = last - first || 1;
  const d = series.map((p, i) => {
    const x = pad + ((p.ord - first) / span) * (w - pad * 2);
    const y = h - pad - ((p.v - 1) / 4) * (h - pad * 2);
    return `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  return `<svg class="rspark" viewBox="0 0 ${w} ${h}" role="img" aria-label="${esc(label)} by month: ${esc(range)}"><path d="${d}"/></svg>`;
}

/** Attributed scopes only. The Worker sends entries for nothing else. */
function entriesList(scope) {
  const entries = Array.isArray(scope.entries) ? scope.entries : [];
  if (!entries.length) return '';
  return `<div class="rentries">${entries.map((e) => `
    <div class="rentry">
      <div class="re-h"><b>${esc(e.by || 'A coach')}</b><small>${esc(when(e.at))}</small>
        ${e.shared ? '<span class="pill good">Shared with the player</span>' : ''}
        ${e.mine ? '<span class="pill hot">Yours</span>' : ''}</div>
      ${e.note ? `<p class="re-note">${esc(e.note)}</p>` : ''}
    </div>`).join('')}</div>`;
}

function when(at) {
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return '';
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/* ------------------------------------------------------------- the form */

function formCard() {
  const f = S.form;
  const people = Array.isArray(S.people) ? S.people : [];
  const picking = f.who !== 'me' && !f.target;
  return `
    <div class="card rform">
      <h3>${f.who === 'me' ? 'Rate yourself' : 'Rate a player'}</h3>
      ${f.who === 'me' ? '' : personPicker(people)}
      ${picking ? '' : `
        ${f.scopes.length > 1 ? `<div class="rchips">${f.scopes.map((s) =>
          `<button type="button" class="chip${f.scope === s ? ' on' : ''}" data-scope-pick="${esc(s)}">${esc(scopeName(s))}</button>`).join('')}</div>` : ''}
        <p class="sub rhelp">${esc(SCOPE_HELP[f.scope] || '')}</p>
        <div class="rpicks">${traitList().map((t) => `
          <div class="rrow">
            <span class="rr-name">${esc(t.label)}</span>
            <span class="rpick" data-key="${esc(t.key)}">${[1, 2, 3, 4, 5].map((v) =>
              `<button type="button" data-key="${esc(t.key)}" data-v="${v}" class="${f.traits[t.key] === v ? 'on' : ''}" aria-pressed="${f.traits[t.key] === v}">${v}</button>`).join('')}</span>
          </div>`).join('')}</div>
        ${f.scope === 'self' || f.scope === 'coach' ? `
          <label class="rlab">Note${f.scope === 'coach' ? ' (the player reads this if you share it)' : ''}
            <textarea data-note rows="2" maxlength="400" placeholder="${f.scope === 'coach' ? 'One thing to work on.' : 'What you noticed in yourself.'}">${esc(f.note)}</textarea></label>` : ''}
        ${f.scope === 'coach' ? `
          <div class="srow rshare">
            <div class="lab"><b>Share this with the player</b><small>Off means it stays with the coaches and parents of this team.</small></div>
            <button type="button" class="sw${f.shared ? ' on' : ''}" data-act="share" aria-pressed="${f.shared}" aria-label="Share this rating with the player"></button>
          </div>` : ''}
        <div class="ractions">
          <button class="btn primary" data-act="save"${f.busy ? ' disabled' : ''}>${f.busy ? 'Saving' : 'Save rating'}</button>
          <button class="btn" data-act="cancel">Cancel</button>
          ${f.existing ? '<button class="btn" data-act="withdraw">Take mine back</button>' : ''}
        </div>
        ${f.existing ? `<p class="rplain">You already rated them on this. Saving updates what you put in, it does not add a second one. A new rating opens on ${esc(when(f.existing.openUntil))}.</p>` : ''}`}
    </div>`;
}

const scopeName = (s) => ({ self: 'Your own', coach: 'As their coach', teammate: 'As a teammate', public: 'Public' }[s] || s);

function personPicker(people) {
  if (S.peopleErr) return `<p class="sub">${esc(S.peopleErr)}</p>`;
  if (!Array.isArray(S.people)) return '<p class="sub">Loading your team.</p>';
  if (!people.length) return `<p class="sub">${esc(S.peopleReason || 'You are not in a family or team yet, so there is nobody to rate.')}</p>`;
  return `<div class="rpeople">${people.map((p) => `
    <button type="button" class="chip${S.form?.target === p.accountId ? ' on' : ''}" data-person="${esc(p.accountId)}">
      ${esc(p.label)}<small>${esc(p.orgName)}</small></button>`).join('')}</div>`;
}

/* ------------------------------------------------------------- behaviour */

function onInput(e) {
  if (e.target.matches('[data-note]') && S.form) S.form.note = e.target.value;
}

/**
 * One root at a time. career.js rebuilds its own markup and hands over a fresh
 * container, and if the container it used before is still an ancestor of the
 * new one then both listeners fire on the same click: a tap on a 3 sets it and
 * then takes it straight back off. Let the old root go before binding the new.
 */
function rebind(el) {
  if (ROOT) {
    ROOT.removeEventListener('click', onClick);
    ROOT.removeEventListener('input', onInput);
  }
  ROOT = el;
  el.addEventListener('click', onClick);
  el.addEventListener('input', onInput);
}

async function onClick(e) {
  const hit = (sel) => e.target.closest(sel);
  const act = hit('[data-act]')?.dataset.act;

  const pick = hit('.rpick button[data-key]');
  if (pick && S.form) {
    const key = pick.dataset.key, v = Number(pick.dataset.v);
    S.form.traits[key] = S.form.traits[key] === v ? undefined : v;
    if (S.form.traits[key] === undefined) delete S.form.traits[key];
    // Repaint just this row, so the page does not jump while they work down it.
    pick.parentElement.querySelectorAll('button').forEach((b) => {
      const on = Number(b.dataset.v) === S.form.traits[key];
      b.classList.toggle('on', on);
      b.setAttribute('aria-pressed', String(on));
    });
    return;
  }

  const person = hit('[data-person]');
  if (person && S.form) { selectPerson(person.dataset.person); return; }

  const scopePick = hit('[data-scope-pick]');
  if (scopePick && S.form) { S.form.scope = scopePick.dataset.scopePick; loadExisting(); paint(); return; }

  if (act === 'retry') return refresh();
  if (act === 'self') return openSelf();
  if (act === 'other') return openOther();
  if (act === 'cancel') { S.form = null; paint(); return; }
  if (act === 'share' && S.form) { S.form.shared = !S.form.shared; paint(); return; }
  if (act === 'save') return save();
  if (act === 'withdraw') return withdraw();
}

function openSelf() {
  const mine = S.data?.mine?.self || null;
  S.form = { who: 'me', target: 'me', label: 'you', scope: 'self', scopes: ['self'],
    traits: { ...(mine?.traits || {}) }, note: mine?.note || '', shared: false, existing: mine, busy: false };
  paint();
}

async function openOther() {
  S.form = { who: 'other', target: null, label: '', scope: 'teammate', scopes: ['teammate'],
    traits: {}, note: '', shared: false, existing: null, busy: false };
  paint();
  if (!Array.isArray(S.people)) {
    try {
      const r = await api('/api/ratings/rateable');
      S.people = Array.isArray(r.people) ? r.people : [];
      S.peopleReason = r.reason || '';
      S.peopleErr = null;
    } catch (err) { S.people = []; S.peopleErr = err.message; }
    if (S.form) paint();
  }
}

async function selectPerson(accountId) {
  const p = (S.people || []).find((x) => x.accountId === accountId);
  if (!p || !S.form) return;
  S.form.target = accountId;
  S.form.label = p.label;
  // The scopes on offer come from the Worker, which decided them from this
  // rater's role in that org. This screen never widens them.
  S.form.scopes = Array.isArray(p.scopes) && p.scopes.length ? p.scopes : ['teammate'];
  S.form.scope = S.form.scopes.includes('coach') ? 'coach' : S.form.scopes[0];
  S.form.traits = {}; S.form.note = ''; S.form.shared = false; S.form.existing = null;
  paint();
  await loadExisting();
}

/** Whatever this rater already put in for this player and scope, to edit. */
async function loadExisting() {
  const f = S.form;
  if (!f || f.who === 'me' || !f.target) return;
  try {
    const r = await api(`/api/ratings/player/${encodeURIComponent(f.target)}`);
    const mine = r?.mine?.[f.scope] || null;
    if (S.form !== f) return;                 // they moved on while this was out
    f.existing = mine;
    if (mine) { f.traits = { ...mine.traits }; f.note = mine.note || ''; f.shared = mine.shared === true; }
    paint();
  } catch { /* a player who cannot read that page can still rate. Leave it blank. */ }
}

async function save() {
  const f = S.form;
  if (!f || f.busy) return;
  if (f.who !== 'me' && !f.target) return toast('Pick who you are rating first.', true);
  if (!Object.keys(f.traits).length) return toast('Rate at least one thing, 1 to 5.', true);
  f.busy = true; paint();
  const body = { scope: f.scope, traits: f.traits };
  if (f.scope === 'self' || f.scope === 'coach') body.note = f.note;
  if (f.scope === 'coach') body.shared = f.shared;
  try {
    await api(`/api/ratings/player/${encodeURIComponent(f.who === 'me' ? 'me' : f.target)}`, { method: 'POST', body: JSON.stringify(body) });
    toast(f.existing ? 'Updated.' : 'Saved.');
    S.form = null;
    await refresh();
  } catch (e) {
    f.busy = false;
    toast(e.message, true);
    paint();
  }
}

async function withdraw() {
  const f = S.form;
  if (!f?.existing) return;
  const gone = C?.ui?.confirm
    ? await C.ui.confirm({ title: 'Take your rating back?', body: 'It comes off their page straight away.', confirmText: 'Take it back', danger: true })
    : true;
  if (!gone) return;
  try {
    await api(`/api/ratings/player/${encodeURIComponent(f.who === 'me' ? 'me' : f.target)}?scope=${encodeURIComponent(f.scope)}`, { method: 'DELETE' });
    toast('Taken back.');
    S.form = null;
    await refresh();
  } catch (e) { toast(e.message, true); }
}
